"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import {
  Card, CardBody, CardHeader, Button, Spinner, Alert, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, Table, Input, FormGroup, Label,
} from "reactstrap"
import { FaLayerGroup, FaPrint, FaTrash, FaExclamationTriangle, FaCheckCircle, FaEye, FaTimes } from "react-icons/fa"
import { toast } from "react-toastify"
import QRCode from "qrcode"
import { listPrinters, silentPrint } from "../../../utils/qzTrayService"
import {
  BADGE_WIDTH,
  BADGE_HEIGHT,
  prepareBadgeCanvas,
  renderBadge,
  loadImageAsync,
  composeGridSheet,
  SHEET_BADGES_PER_PAGE,
  SHEET_DPI_SCALE,
  A4_PAGE_SIZE_IN,
} from "./badgeRenderer"
import {
  consolidatedPrintService,
  type ConsolidatedDashboardSummary,
  type ConsolidatedQueue,
  type ConsolidatedBadgeResult,
} from "../../../Service/consolidated-print-service"

const dynamicImage = (imagePath: string): string => `/assets/images/${imagePath}`

// ---------------------------------------------------------------------------
// Consolidated Remaining Badges
//
// Bulk Printing only ever prints complete 2x2 sheets for ONE print order, so
// 1-3 leftover badges per order stay pending. This screen sweeps those
// leftovers in from EVERY company/print order in the exhibition into one
// shared queue, and prints them together using the exact same 4-per-sheet
// layout — reusing the shared badge renderer/printing utilities that
// bulkprint.tsx also uses, so the output is pixel-identical.
// ---------------------------------------------------------------------------

const DPI_SCALE = SHEET_DPI_SCALE

const ConsolidatedRemainingBadges = () => {
  const [summary, setSummary] = useState<ConsolidatedDashboardSummary | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(true)
  const [creating, setCreating] = useState(false)

  const [queue, setQueue] = useState<ConsolidatedQueue | null>(null)
  const [loadingQueue, setLoadingQueue] = useState(false)

  const [badgeResults, setBadgeResults] = useState<ConsolidatedBadgeResult[]>([])
  const [pagePreviews, setPagePreviews] = useState<string[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0 })

  const [availablePrinters, setAvailablePrinters] = useState<string[]>([])
  const [selectedPrinter, setSelectedPrinter] = useState("")

  const [showForceConfirm, setShowForceConfirm] = useState(false)
  const [isForcing, setIsForcing] = useState(false)

  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)

  const refreshDashboard = useCallback(async () => {
    setLoadingSummary(true)
    try {
      const res = await consolidatedPrintService.getDashboard()
      setSummary(res.summary)
    } catch (err: any) {
      toast.error(err?.message || "Failed to load consolidated badge dashboard")
    } finally {
      setLoadingSummary(false)
    }
  }, [])

  useEffect(() => {
    refreshDashboard()
  }, [refreshDashboard])

  useEffect(() => {
    listPrinters()
      .then((printers) => {
        const names = printers.map((p) => p.name)
        setAvailablePrinters(names)
        setSelectedPrinter((current) => current || names[0] || "")
      })
      .catch(() => {
        // Non-fatal — surfaced only when the operator tries to print.
      })
  }, [])

  const refreshQueue = useCallback(async (consolidatedOrderId: number) => {
    setLoadingQueue(true)
    try {
      const res = await consolidatedPrintService.getOrder(consolidatedOrderId)
      setQueue(res.queue)
    } catch (err: any) {
      toast.error(err?.message || "Failed to load consolidated queue")
    } finally {
      setLoadingQueue(false)
    }
  }, [])

  const handleCreateOrder = async () => {
    setCreating(true)
    try {
      const res = await consolidatedPrintService.createOrder()
      toast.success(res.message)
      setQueue(res.queue)
      setBadgeResults([])
      setPagePreviews([])
      await refreshDashboard()
    } catch (err: any) {
      toast.error(err?.message || "Failed to create consolidated print order")
    } finally {
      setCreating(false)
    }
  }

  const handleRemoveItem = async (itemId: number) => {
    try {
      await consolidatedPrintService.removeItem(itemId)
      toast.info("Badge removed from the queue — it will reappear as pending.")
      if (queue) await refreshQueue(queue.consolidated_order_id)
      await refreshDashboard()
    } catch (err: any) {
      toast.error(err?.message || "Failed to remove badge")
    }
  }

  const handleCancelOrder = async () => {
    if (!queue) return
    if (!window.confirm("Cancel this consolidated print order? All its pending badges will return to the eligible pool.")) return
    try {
      await consolidatedPrintService.cancelOrder(queue.consolidated_order_id)
      toast.info("Consolidated print order cancelled.")
      setQueue(null)
      setBadgeResults([])
      setPagePreviews([])
      await refreshDashboard()
    } catch (err: any) {
      toast.error(err?.message || "Failed to cancel consolidated print order")
    }
  }

  // ---- Badge rendering (identical drawing code to bulkprint.tsx, via the
  // shared badgeRenderer module — no copy-pasted canvas logic here) ----
  const generateSingleBadge = useCallback(async (badgeData: ConsolidatedBadgeResult["badge_data"]): Promise<string> => {
    const canvas = hiddenCanvasRef.current
    if (!canvas) throw new Error("Canvas element not found")

    const qrImageElement = await QRCode.toDataURL(badgeData.badge_info.verification_code, {
      width: 120 * DPI_SCALE,
      margin: 2,
      errorCorrectionLevel: "H",
      type: "image/png",
      color: { dark: "#000000", light: "#FFFFFF" },
    }).then((qrUrl) => loadImageAsync(qrUrl))

    const photoImage = badgeData.has_photo && badgeData.photo_url
      ? await loadImageAsync(badgeData.photo_url).catch(() => null)
      : null

    const psfLogoImage = await loadImageAsync(dynamicImage("logo.PNG")).catch(() => null)

    const ctx = prepareBadgeCanvas(canvas, DPI_SCALE)
    renderBadge(
      ctx,
      {
        badge_info: {
          person_full_name: badgeData.badge_info.person_full_name,
          role: badgeData.badge_info.role,
          exhibitor_name: badgeData.badge_info.exhibitor_name,
          exhibition_title: badgeData.badge_info.exhibition_title,
          stand_category: badgeData.badge_info.stand_category,
          role_color: badgeData.badge_info.role_color,
        },
        has_photo: badgeData.has_photo,
      },
      { photoImage, qrImage: qrImageElement, logoImage: psfLogoImage, showLogo: false },
    )
    return canvas.toDataURL("image/png", 1.0)
  }, [])

  const composePages = async (images: string[]): Promise<string[]> => {
    const loaded = await Promise.all(images.map((src) => loadImageAsync(src)))
    const pages: string[] = []
    for (let i = 0; i < loaded.length; i += SHEET_BADGES_PER_PAGE) {
      const slice = loaded.slice(i, i + SHEET_BADGES_PER_PAGE)
      pages.push(composeGridSheet(slice, BADGE_WIDTH, BADGE_HEIGHT, DPI_SCALE))
    }
    return pages
  }

  const runGeneration = async (mode: "normal" | "force") => {
    if (!queue) return
    setIsGenerating(true)
    setBadgeResults([])
    setPagePreviews([])
    try {
      const res = mode === "normal"
        ? await consolidatedPrintService.generateBatch(queue.consolidated_order_id)
        : await consolidatedPrintService.forcePrintRemaining(queue.consolidated_order_id)

      if (!res.success) throw new Error(res.message)

      const results = res.data.badge_results || []
      if (res.data.failed_badges?.length) {
        toast.warning(`${res.data.failed_badges.length} badge(s) failed to generate.`)
      }
      setBadgeResults(results)

      const images: string[] = []
      for (const r of results) {
        try {
          images.push(await generateSingleBadge(r.badge_data))
        } catch {
          images.push("")
        }
      }

      toast.success(`Generated ${images.filter(Boolean).length} of ${results.length} badge(s).`)

      setIsComposing(true)
      try {
        const validImages = images.filter(Boolean)
        setPagePreviews(await composePages(validImages))
      } finally {
        setIsComposing(false)
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate consolidated badges")
    } finally {
      setIsGenerating(false)
    }
  }

  const dataUrlToBase64 = (dataUrl: string) => dataUrl.substring(dataUrl.indexOf(",") + 1)

  const handlePrint = async () => {
    if (!selectedPrinter) {
      toast.error("Select a printer before printing.")
      return
    }
    if (pagePreviews.length === 0) {
      toast.error("No composed pages to print yet.")
      return
    }
    setIsPrinting(true)
    setPrintProgress({ current: 0, total: pagePreviews.length })
    let successPages = 0
    for (let i = 0; i < pagePreviews.length; i++) {
      try {
        await silentPrint({
          printerName: selectedPrinter,
          base64Data: dataUrlToBase64(pagePreviews[i]),
          format: "image",
          size: A4_PAGE_SIZE_IN,
        })
        successPages++
      } catch (err: any) {
        toast.error(`Failed to print page ${i + 1}: ${err?.message || "Unknown error"}`)
      }
      setPrintProgress({ current: i + 1, total: pagePreviews.length })
    }
    setIsPrinting(false)

    if (successPages === pagePreviews.length) {
      toast.success(`All ${pagePreviews.length} page(s) sent to ${selectedPrinter} successfully!`)
      if (queue) await refreshQueue(queue.consolidated_order_id)
      await refreshDashboard()
    } else {
      toast.warning(`${successPages} of ${pagePreviews.length} pages printed successfully.`)
    }
  }

  const handleComplete = async () => {
    if (!queue) return
    try {
      const res = await consolidatedPrintService.completeOrder(queue.consolidated_order_id)
      toast.success(res.message)
      setQueue(null)
      setBadgeResults([])
      setPagePreviews([])
      await refreshDashboard()
    } catch (err: any) {
      toast.error(err?.message || "Failed to complete consolidated print order")
    }
  }

  const handleEnableForcePrint = async () => {
    if (!queue) return
    try {
      await consolidatedPrintService.enableForcePrint(queue.consolidated_order_id)
      toast.info("Force Print enabled for this consolidated order.")
      await refreshQueue(queue.consolidated_order_id)
    } catch (err: any) {
      toast.error(err?.message || "Failed to enable Force Print (administrator permission required).")
    }
  }

  const confirmForcePrint = async () => {
    setShowForceConfirm(false)
    setIsForcing(true)
    try {
      await runGeneration("force")
    } finally {
      setIsForcing(false)
    }
  }

  // ---- Render ----

  return (
    <div>
      <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />

      <Card className="mb-4">
        <CardHeader className="d-flex align-items-center justify-content-between">
          <span><FaLayerGroup className="me-2" />Consolidated Remaining Badges</span>
          {!loadingSummary && (
            <Button color="link" size="sm" onClick={refreshDashboard}>Refresh</Button>
          )}
        </CardHeader>
        <CardBody>
          {loadingSummary ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : summary ? (
            <div className="row text-center g-3">
              <div className="col"><h4>{summary.total_pending_badges}</h4><small className="text-muted">Total Pending Badges</small></div>
              <div className="col"><h4>{summary.contributing_companies}</h4><small className="text-muted">Companies</small></div>
              <div className="col"><h4>{summary.original_print_orders}</h4><small className="text-muted">Original Print Orders</small></div>
              <div className="col"><h4>{summary.printable_sheets}</h4><small className="text-muted">Printable Sheets</small></div>
              <div className="col"><h4>{summary.remaining_after_grouping}</h4><small className="text-muted">Remaining After Grouping</small></div>
            </div>
          ) : (
            <Alert color="warning" className="mb-0">Unable to load dashboard.</Alert>
          )}

          {!loadingSummary && summary && summary.not_yet_queued === 0 && !queue && (
            <Alert color="success" className="mt-3 mb-0">
              <FaCheckCircle className="me-2" /> No un-queued pending badges right now — nothing to consolidate.
            </Alert>
          )}

          {!queue && (
            <div className="text-center mt-4">
              <Button
                color="primary"
                size="lg"
                disabled={creating || loadingSummary || !summary || summary.not_yet_queued === 0}
                onClick={handleCreateOrder}
              >
                {creating ? <Spinner size="sm" className="me-2" /> : <FaLayerGroup className="me-2" />}
                Create Consolidated Print Order
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {queue && (
        <Card className="mb-4">
          <CardHeader className="d-flex align-items-center justify-content-between">
            <span>Consolidated Queue — {queue.order_number}</span>
            <div>
              <Badge color={queue.status === "printing" ? "warning" : "secondary"} className="me-2">{queue.status}</Badge>
              <Button color="outline-danger" size="sm" onClick={handleCancelOrder}>
                <FaTimes className="me-1" /> Cancel Queue
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loadingQueue ? (
              <div className="text-center py-4"><Spinner /></div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col"><strong>{queue.total_pending}</strong> pending badge{queue.total_pending !== 1 ? "s" : ""}</div>
                  <div className="col"><strong>{queue.full_sheets}</strong> complete sheet{queue.full_sheets !== 1 ? "s" : ""} ready</div>
                  <div className="col"><strong>{queue.remainder}</strong> left over (needs Force Print)</div>
                </div>

                {queue.companies.map((group) => (
                  <div key={group.exhibitor_id} className="mb-3">
                    <h6 className="mb-2">{group.exhibitor_name}</h6>
                    <Table size="sm" bordered responsive>
                      <thead>
                        <tr>
                          <th>Badge</th>
                          <th>Original Order</th>
                          <th style={{ width: 90 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.badges.map((b) => (
                          <tr key={b.item_id}>
                            <td>{b.person_full_name}</td>
                            <td>{b.original_order_number || `#${b.original_print_order_id}`}</td>
                            <td>
                              <Button color="link" size="sm" className="text-danger p-0" onClick={() => handleRemoveItem(b.item_id)}>
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ))}

                <hr />

                <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                  <FormGroup className="mb-0">
                    <Label className="me-2 mb-0">Printer</Label>
                    <Input
                      type="select"
                      style={{ width: 220, display: "inline-block" }}
                      value={selectedPrinter}
                      onChange={(e) => setSelectedPrinter(e.target.value)}
                    >
                      {availablePrinters.length === 0 && <option value="">No printers found</option>}
                      {availablePrinters.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Input>
                  </FormGroup>

                  <Button
                    color="primary"
                    disabled={isGenerating || queue.full_sheets === 0}
                    onClick={() => runGeneration("normal")}
                  >
                    {isGenerating ? <Spinner size="sm" className="me-2" /> : <FaEye className="me-2" />}
                    Generate Previews ({queue.full_sheets * 4} badges)
                  </Button>

                  {queue.remainder > 0 && !queue.force_print && (
                    <Button color="outline-warning" onClick={handleEnableForcePrint}>
                      Enable Force Print
                    </Button>
                  )}
                  {queue.remainder > 0 && queue.force_print && (
                    <Button color="warning" disabled={isForcing} onClick={() => setShowForceConfirm(true)}>
                      {isForcing ? <Spinner size="sm" className="me-2" /> : <FaExclamationTriangle className="me-2" />}
                      Force Print Remaining Badges ({queue.remainder})
                    </Button>
                  )}
                </div>

                {isComposing && <div className="text-center py-3"><Spinner size="sm" className="me-2" />Composing print sheets…</div>}

                {pagePreviews.length > 0 && (
                  <>
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      {pagePreviews.map((img, idx) => (
                        <div key={idx} style={{ width: 220 }}>
                          <div className="small text-muted mb-1">Sheet {idx + 1} of {pagePreviews.length}</div>
                          <img src={img || "/placeholder.svg"} alt={`Sheet ${idx + 1}`} className="border rounded w-100" />
                        </div>
                      ))}
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <Button color="success" disabled={isPrinting} onClick={handlePrint}>
                        {isPrinting ? <Spinner size="sm" className="me-2" /> : <FaPrint className="me-2" />}
                        Print {pagePreviews.length} Sheet{pagePreviews.length !== 1 ? "s" : ""}
                      </Button>
                      {isPrinting && (
                        <span className="small text-muted">{printProgress.current}/{printProgress.total} sheets sent</span>
                      )}
                      {queue.remainder === 0 && (
                        <Button color="outline-success" onClick={handleComplete}>
                          <FaCheckCircle className="me-2" /> Mark Consolidated Order Complete
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </CardBody>
        </Card>
      )}

      <Modal isOpen={showForceConfirm} toggle={() => setShowForceConfirm(false)}>
        <ModalHeader toggle={() => setShowForceConfirm(false)}>Force Print Remaining Badges?</ModalHeader>
        <ModalBody>
          <Alert color="warning" className="mb-0">
            <FaExclamationTriangle className="me-2" />
            This will print a final sheet with fewer than 4 badges — some paper space
            will be unused. This action cannot be undone. Continue?
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowForceConfirm(false)}>Cancel</Button>
          <Button color="warning" onClick={confirmForcePrint}>Yes, Force Print</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ConsolidatedRemainingBadges
